import { useState, useEffect, useRef } from 'react';

/**
 * Polls GET /api/schedule/versions and compares against the versions
 * loaded by the component. Returns stale status for watched sections.
 *
 * Skips polling when the browser tab is hidden.
 */
export function useScheduleVersionCheck(loadedVersions, watchSections, intervalMs = 15000) {
  const [staleInfo, setStaleInfo] = useState({ isStale: false, staleSections: [] });
  const loadedRef = useRef(loadedVersions);
  const watchRef = useRef(watchSections);

  useEffect(() => { loadedRef.current = loadedVersions; }, [loadedVersions]);
  useEffect(() => { watchRef.current = watchSections; }, [watchSections]);

  useEffect(() => {
    if (!loadedRef.current || Object.keys(loadedRef.current).length === 0) return;

    const check = async () => {
      if (document.hidden) return;
      try {
        const res = await fetch('/api/schedule/versions', { cache: 'no-store' });
        if (!res.ok) return;
        const serverVersions = await res.json();
        const stale = watchRef.current.filter(
          s => serverVersions[s] !== undefined && serverVersions[s] > (loadedRef.current[s] || 0)
        );
        setStaleInfo({ isStale: stale.length > 0, staleSections: stale });
      } catch {
        // Network error -- silently skip this cycle
      }
    };

    const id = setInterval(check, intervalMs);
    check();
    return () => clearInterval(id);
  }, [intervalMs]);

  return staleInfo;
}
